import {
  PRICE_REPRESENTATION_MULTIPLIER,
  type PriceFormat
} from '@/domain/Pricing';
import type { CustomFormatters } from '@/utils/createI18nInstance';
import useMarketConfig from './useMarketConfig.tsx';

type PriceFormatter = (value: number, removeTrailingZeros?: boolean) => string;

// Note: These defaults only serve to have them as a reference to then substitute
// for those that come in the marketConfig.
const DEFAULTS = {
  locale: 'en-US',
  decimal: '.',
  thousands: ','
};

// JSON Reference: https://dsu-bitbucket.nestle.biz/projects/ECAPI/repos/ecom-api-clients/browse/data-real/currencySymbols.json
const CURRENCIES_TO_DISPLAY_SYMBOL = new Set([
  'AUD',
  'ARS',
  'BRL',
  'CAD',
  'CHF',
  'CLP',
  'COP',
  'CZK',
  'EUR',
  'GBP',
  'HUF',
  'JPY',
  'KRW',
  'MXN',
  'NZD',
  'PLN',
  'RUB',
  'THB',
  'TWD',
  'UAH',
  'USD'
]);

export default function usePriceFormatter(): PriceFormatter {
  const marketConfig = useMarketConfig();

  return getPriceFormatter(
    marketConfig.financialSettings.defaultCurrency,
    marketConfig.priceFormat
  );
}

export function getPriceFormatter(
  currency: string,
  priceFormat: PriceFormat
): PriceFormatter {
  const {
    decimalSeparator,
    thousandsGroupingSeparator,
    minusSign,
    minimumFractionDigits,
    maximumFractionDigits,
    pattern
  } = priceFormat;

  function format(value: number, removeTrailingZeros = false): string {
    const amount = value / PRICE_REPRESENTATION_MULTIPLIER;
    const hasTrailingZeros = amount % 1 === 0;
    const formatter = new Intl.NumberFormat(DEFAULTS.locale, {
      style: 'decimal',
      minimumFractionDigits:
        removeTrailingZeros && hasTrailingZeros ? 0 : minimumFractionDigits,
      maximumFractionDigits:
        removeTrailingZeros && hasTrailingZeros ? 0 : maximumFractionDigits
    });
    const formattedNumber = formatter
      .format(Math.abs(amount))
      .replaceAll(DEFAULTS.thousands, thousandsGroupingSeparator)
      .replace(DEFAULTS.decimal, decimalSeparator);

    return pattern
      .replace('{sign}', amount < 0 ? minusSign : '')
      .replace('{amount}', formattedNumber)
      .replace('{currency}', getCurrencySymbol(currency));
  }

  return format;
}

export function getI18nPriceFormatter(
  currency: string,
  priceFormat: PriceFormat
): CustomFormatters {
  const format = getPriceFormatter(currency, priceFormat);
  return {
    price: (_lang: string | undefined, options: any) => (v) =>
      format(Number(v), Boolean(options?.withoutTrailingZeros))
  };
}

function getCurrencySymbol(currencyISO: string): string {
  if (!CURRENCIES_TO_DISPLAY_SYMBOL.has(currencyISO)) return currencyISO;

  const formatter = new Intl.NumberFormat(DEFAULTS.locale, {
    style: 'currency',
    currencyDisplay: 'narrowSymbol',
    currency: currencyISO
  });

  return formatter.formatToParts(1).find((p) => p.type === 'currency')!.value;
}
